// Organizations tab (platform superadmin) — onboard orgs, manage their invite
// sender, and verify the email domains they're allowed to send from (Phase 5).
import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, useTheme,
} from "@mui/material";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { tokens } from "../../../theme";
import { MONO_FONT } from "../data/mockUsersRoles";
import {
  getOrganizations, createOrganization, addOrgDomain, verifyOrgDomain,
} from "../api";

const OrganizationsTab = ({ showToast }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newName, setNewName] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [creating, setCreating] = useState(false);
  const [domainInputs, setDomainInputs] = useState({}); // orgName -> domain text
  const [busyDomain, setBusyDomain] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOrgs(await getOrganizations());
    } catch (err) {
      setError("Could not load organizations. Platform superadmin role required.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return showToast("Organization name is required", "warning");
    setCreating(true);
    try {
      await createOrganization(
        newName.trim(),
        newDomain.trim() ? [newDomain.trim().toLowerCase()] : []
      );
      showToast(`Organization "${newName.trim()}" created`, "success");
      setNewName(""); setNewDomain("");
      await load();
    } catch (err) {
      showToast(`Could not create organization: ${err.message || "error"}`, "error");
    } finally {
      setCreating(false);
    }
  };

  // Add a domain, then immediately verify it (manual override is allowed for
  // superadmins locally; in production this checks the DNS TXT record).
  const handleAddVerify = async (orgName) => {
    const domain = (domainInputs[orgName] || "").trim().toLowerCase();
    if (!domain) return showToast("Enter a domain", "warning");
    setBusyDomain(orgName);
    try {
      const rec = await addOrgDomain(domain, orgName);
      showToast(`Add TXT ${rec.recordName}: ${rec.recordValue}`, "info");
      const res = await verifyOrgDomain(domain, orgName, true);
      if (res.verified) showToast(`${domain} verified for ${orgName}`, "success");
      setDomainInputs((p) => ({ ...p, [orgName]: "" }));
      await load();
    } catch (err) {
      showToast(`Domain step failed: ${err.message || "error"}`, "error");
    } finally {
      setBusyDomain(null);
    }
  };

  const panel = {
    backgroundColor: "var(--fx-paper)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    overflow: "hidden",
  };
  const labelSx = {
    fontSize: "0.625rem", fontWeight: 600, letterSpacing: "0.04em",
    textTransform: "uppercase", color: colors.grey[500], mb: 0.5,
  };

  return (
    <Box>
      {/* Onboard new org */}
      <Box sx={{ ...panel, mb: 2.5 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--fx-text-primary)" }}>
            Onboard an organization
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[500], mt: 0.2 }}>
            Each org manages its own members and sends invites from its own verified domain.
          </Typography>
        </Box>
        <Box sx={{ p: 2, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={labelSx}>Business name *</Typography>
            <TextField fullWidth size="small" placeholder="e.g. SickKids" value={newName} onChange={(e) => setNewName(e.target.value)} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={labelSx}>Primary domain</Typography>
            <TextField fullWidth size="small" placeholder="sickkids.ca" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} />
          </Box>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? "Creating…" : "Create organization"}
          </Button>
        </Box>
      </Box>

      {/* Org list */}
      <Box sx={panel}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--fx-text-primary)" }}>
            Organizations
          </Typography>
          <Button size="small" variant="outlined" onClick={load} sx={{ fontSize: "0.6875rem", py: 0.25 }}>
            Refresh
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress size={22} /></Box>
        ) : error ? (
          <Typography sx={{ p: 3, textAlign: "center", fontSize: "0.8125rem", color: "#dc2626" }}>{error}</Typography>
        ) : !orgs.length ? (
          <Typography sx={{ p: 3, textAlign: "center", fontSize: "0.75rem", color: colors.grey[500] }}>No organizations yet</Typography>
        ) : (
          <Table size="small" sx={{ "& td, & th": { fontSize: "0.75rem", borderColor: theme.palette.divider, verticalAlign: "top" } }}>
            <TableHead>
              <TableRow sx={{ "& th": { color: colors.grey[500], fontWeight: 600, py: 1 } }}>
                <TableCell>Organization</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Invite sender</TableCell>
                <TableCell>Domains</TableCell>
                <TableCell>Add &amp; verify domain</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell sx={{ color: "var(--fx-text-primary)", fontWeight: 600 }}>{o.name}</TableCell>
                  <TableCell sx={{ fontFamily: MONO_FONT }}>{o.memberCount}</TableCell>
                  <TableCell>
                    {o.sender ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontFamily: MONO_FONT, color: o.senderAligned ? "#16a34a" : "#d97706" }}>
                        {o.senderAligned && <VerifiedOutlinedIcon sx={{ fontSize: 14 }} />}
                        {o.sender}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[500] }}>platform default</Typography>
                    )}
                    {o.sender && !o.senderAligned && (
                      <Typography sx={{ fontSize: "0.5625rem", color: "#d97706" }}>domain not verified — falls back to platform</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {(o.domains || []).map((d) => {
                        const ok = (o.verifiedDomains || []).includes(d);
                        return (
                          <Chip key={d} size="small" label={d}
                            icon={ok ? <VerifiedOutlinedIcon sx={{ fontSize: 13 }} /> : undefined}
                            sx={{
                              height: 20, fontSize: "0.625rem", fontFamily: MONO_FONT,
                              color: ok ? "#16a34a" : colors.grey[500],
                              backgroundColor: ok ? "rgba(22,163,74,0.1)" : "rgba(100,116,139,0.1)",
                            }} />
                        );
                      })}
                      {!(o.domains || []).length && (
                        <Typography sx={{ fontSize: "0.625rem", color: colors.grey[500] }}>none</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      <TextField size="small" placeholder="domain.com"
                        value={domainInputs[o.name] || ""}
                        onChange={(e) => setDomainInputs((p) => ({ ...p, [o.name]: e.target.value }))}
                        sx={{ "& .MuiInputBase-input": { fontSize: "0.6875rem", py: 0.5 } }} />
                      <Button size="small" variant="outlined" disabled={busyDomain === o.name}
                        onClick={() => handleAddVerify(o.name)}
                        sx={{ minHeight: 26, py: 0.25, fontSize: "0.625rem", whiteSpace: "nowrap" }}>
                        {busyDomain === o.name ? "…" : "Add + verify"}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
};

export default OrganizationsTab;
